/* eslint-disable @typescript-eslint/no-explicit-any */
import type { AppThunk } from '@src/redux/appThunk.ts';
import FJRequirementsUtil from '@src/util/FJRequirementsUtil';
import { getLtsUploadSource, LTS_PARSE_FAIL, setLtsUploadSource, LTS_SOURCE } from '@src/util/FJLongToShortEa';
import FJUtil from '@src/util/FJUtil';
import { setIsVerifyingToGoogleDrive } from '@src/redux/slice/FJStartLandingSlice.ts';
import FJUserUtil from '@src/util/FJUserUtil';
import { FJModalsAction } from '@src/redux/action/FJModalsAction.ts';
import FJShareSlice from '@src/redux/slice/FJShareSlice.ts';
import { ACCESS_TOKEN_STATUS, SHARE_BINDING_STATUS, SOCIAL_KEY } from '@src/constants/FJShareSocialConstant.ts';
import FJLocalStore from '@src/store/FJLocalStore';
import FJMessageStore, { MESSAGE_TYPE } from '@src/store/FJMessageStore';
import FJNetworkManager from '@src/core/FJNetworkManager';
import { FJGeneratingPageAction } from '@src/redux/action/FJGeneratingPageAction.ts';
import { FJStartLandingAction } from '@src/redux/action/FJStartLandingAction.ts';
import FJPublishAction from '@src/redux/action/FJPublishAction.ts';
import FJShareStore from '@src/store/FJShareStore';

let _downloadAbortController: AbortController | null = null;

const FJGoogleDriveAction = {
    authenticationGoogleDriveAction: (): AppThunk => async (dispatch, getState) => {
        void getState();
        dispatch(FJStartLandingAction.clearProjectSelectionAction());
        const isLinked = !!FJUserUtil.getAccessTokenWithType(SOCIAL_KEY.GOOGLE_DRIVE);
        FJRequirementsUtil.eaLog('google_drive_click', { whether_first: !isLinked });

        if (!navigator.onLine) {
            FJMessageStore.addMessage(FJLocalStore._('network-error-please-try-again'), 'error');
            return;
        }

        if (!FJRequirementsUtil.isLogin() && !FJUtil.isLocalhost()) {
            await FJRequirementsUtil.anonymousSignUp();
            return;
        }

        dispatch(setIsVerifyingToGoogleDrive(true));

        const data = await FJShareStore.checkAccessToken(SOCIAL_KEY.GOOGLE_DRIVE).catch(() => {
            FJMessageStore.addMessage(FJLocalStore._('google drive authentication failed'), MESSAGE_TYPE.ERROR);
        });
        dispatch(setIsVerifyingToGoogleDrive(false));
        if (!data) {
            // TODO 返回报错
            return;
        }

        FJUserUtil.updateUserAccountState(SOCIAL_KEY.GOOGLE_DRIVE, data);
        dispatch(
            FJShareSlice.actions.updateShareState({
                shareType: SOCIAL_KEY.GOOGLE_DRIVE,
                data: {
                    linkAccountState: SHARE_BINDING_STATUS.LINKED,
                    accessTokenState: ACCESS_TOKEN_STATUS.VALID,
                },
            }),
        );
        dispatch(FJModalsAction.showGoogleDriveImportModalAction());
    },
    unlinkAction: (): AppThunk => async (dispatch, getState) => {
        void getState();
        dispatch(FJPublishAction.unlinkAction(SOCIAL_KEY.GOOGLE_DRIVE));
    },
    selectGoogleDriveFileAction:
        (data: any): AppThunk =>
        async (dispatch, getState) => {
            void getState();
            dispatch(FJStartLandingAction.clearProjectSelectionAction());

            const { type, name, url, duration, mimeType } = data;
            setLtsUploadSource(LTS_SOURCE.GOOGLE_DRIVE);
            FJRequirementsUtil.eaLog('long_to_shorts_vedio_upload', { source: getLtsUploadSource() });

            if (type !== 'VIDEO') {
                FJRequirementsUtil.eaLog('long_to_shorts_vedio_parse_fail', {
                    source: getLtsUploadSource(),
                    fail_reason: LTS_PARSE_FAIL.GDRIVE_NON_VIDEO,
                });
                dispatch(FJModalsAction.hideGoogleDriveImportModalAction());
                return;
            }

            if (duration < 60 || duration > 60 * 120) {
                FJRequirementsUtil.eaLog('long_to_shorts_vedio_parse_fail', {
                    source: getLtsUploadSource(),
                    fail_reason: LTS_PARSE_FAIL.GDRIVE_DURATION_INVALID,
                });
                FJMessageStore.addMessage(FJLocalStore._('please-upload-a-video-between-1-and-120-minutes'), 'error');
                dispatch(FJModalsAction.hideGoogleDriveImportModalAction());
                return;
            }
            FJRequirementsUtil.eaLog('google_drive_upload_select_click');
            dispatch(FJModalsAction.hideGoogleDriveImportModalAction());
            const accessToken = FJUserUtil.getAccessTokenWithType(SOCIAL_KEY.GOOGLE_DRIVE);
            let isCancel = false;
            dispatch(
                FJModalsAction.showProgressModalAction(
                    {
                        messageKey: FJLocalStore._('loading'),
                        hint: FJLocalStore._('please-dont-close-this-window'),
                        progress: 0,
                    },
                    () => {
                        FJRequirementsUtil.eaLog('google_drive_download_window_close');
                        _downloadAbortController?.abort();
                        _downloadAbortController = null;
                        isCancel = true;
                    },
                ),
            );
            _downloadAbortController = new AbortController();
            FJNetworkManager.fetchBlob(
                url,
                accessToken,
                ({ loaded, total }: { loaded: number; total: number }) => {
                    const progress = Math.round((loaded / total) * 100 * 10) / 10;
                    dispatch(FJModalsAction.updateProgressModalAction({ progress }));
                },
                _downloadAbortController.signal,
            )
                .then(blob => {
                    const fileName = name || `${type}${Date.now()}.mp4`;
                    const file = new File([blob], fileName, { type: mimeType });

                    dispatch(FJGeneratingPageAction.setVideoMetaAction(file));
                })
                .catch(() => {
                    if (!isCancel) {
                        FJRequirementsUtil.eaLog('long_to_shorts_vedio_parse_fail', {
                            source: getLtsUploadSource(),
                            fail_reason: LTS_PARSE_FAIL.FETCH_BLOB_FAIL,
                        });
                        FJMessageStore.addMessage(FJLocalStore._('network-error-please-try-again'), MESSAGE_TYPE.ERROR);
                    } else {
                        FJRequirementsUtil.eaLog('long_to_shorts_vedio_parse_fail', {
                            source: getLtsUploadSource(),
                            fail_reason: LTS_PARSE_FAIL.CANCEL,
                        });
                    }
                    dispatch(FJModalsAction.hideProgressModalAction());
                });
        },
};

export default FJGoogleDriveAction;
